import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Input from '../components/UI/Input';
import Button from '../components/UI/Button';
import { Phone, Lock, ArrowLeft } from 'lucide-react';

const LoginPage: React.FC = () => {
  const [step, setStep] = useState<'phone' | 'otp'>('phone');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const { login, sendOtp } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Redirect after login
  const from = (location.state as any)?.from?.pathname || '/dashboard';

  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (phone.length < 11) {
      setError('شماره موبایل معتبر نیست');
      return;
    }
    setLoading(true);
    setError('');
    
    const result = await sendOtp(phone);
    setLoading(false);
    
    if (result.success) {
      setStep('otp');
    } else {
      setError(result.error || 'خطا در ارسال کد تایید');
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    const success = await login(phone, otp, 'phone');
    setLoading(false);
    
    if (success) {
      navigate(from, { replace: true });
    } else {
      setError('کد وارد شده صحیح نیست');
    }
  };

  const handleGoogleLogin = async () => {
    setLoading(true);
    // Simulate Google Login delay
    setTimeout(async () => {
      const success = await login('user@gmail.com', '', 'google');
      setLoading(false);
      if (success) {
        navigate(from, { replace: true });
      }
    }, 1500);
  };

  return (
    <div className="min-h-[calc(100vh-64px)] flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Decorative background */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0">
          <div className="absolute top-10 right-10 w-72 h-72 bg-teal-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob"></div>
          <div className="absolute top-10 left-10 w-72 h-72 bg-purple-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-2000"></div>
          <div className="absolute bottom-10 left-20 w-72 h-72 bg-pink-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-4000"></div>
      </div>

      <div className="max-w-md w-full space-y-8 bg-white/80 backdrop-blur-lg p-10 rounded-2xl shadow-xl z-10 border border-white">
        <div>
          <h2 className="mt-2 text-center text-3xl font-extrabold text-gray-900">
            {step === 'phone' ? 'ورود به حساب کاربری' : 'تایید شماره موبایل'}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            {step === 'phone' 
              ? 'برای دسترسی به پروفایل و مدیریت آگهی‌ها لطفا وارد شوید.' 
              : `کد تایید به شماره ${phone} ارسال شد.`}
          </p>
        </div>
        
        {step === 'phone' ? (
          <>
            <form className="mt-8 space-y-6" onSubmit={handleSendOtp}>
              <div className="rounded-md shadow-sm -space-y-px">
                <Input
                  label="شماره موبایل"
                  type="tel"
                  placeholder="0912..."
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  required
                  className="text-left"
                  dir="ltr"
                />
              </div>
              {error && <p className="text-gray-600 text-sm text-center">{error}</p>}
              <Button type="submit" fullWidth disabled={loading}>
                {loading ? 'در حال ارسال...' : 'ارسال کد تایید'}
              </Button>
            </form>

            <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-white text-gray-500">یا</span>
                </div>
            </div>

            <button 
               type="button" 
               onClick={handleGoogleLogin}
               disabled={loading}
               className="w-full flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 rounded-3xl shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            >
               <svg className="h-5 w-5" aria-hidden="true" viewBox="0 0 24 24">
                  <path d="M12.0003 20.45c4.6667 0 8.45-3.7833 8.45-8.45 0-4.6667-3.7833-8.45-8.45-8.45-4.6667 0-8.45 3.7833-8.45 8.45 0 4.6667 3.7833 8.45 8.45 8.45Z" fill="#fff"></path>
                  <path d="M20.3168 11.233H12.0003V14.5497H16.8998C16.6331 16.1497 15.3498 17.5164 12.0003 17.5164C9.18362 17.5164 6.80029 15.333 6.80029 12.0164C6.80029 8.69971 9.18362 6.51638 12.0003 6.51638C13.4336 6.51638 14.6169 7.08304 15.4836 7.96638L17.8003 5.64971C16.3503 4.23304 14.3669 3.19971 12.0003 3.19971C7.13362 3.19971 3.18359 7.14971 3.18359 12.0164C3.18359 16.883 7.13362 20.833 12.0003 20.833C16.4836 20.833 20.5836 17.5997 20.5836 12.583C20.5836 12.033 20.5336 11.6664 20.3168 11.233Z" fill="#EA4335"></path>
               </svg>
               ورود با حساب گوگل (Gmail)
            </button>
          </>
        ) : (
          <form className="mt-8 space-y-6" onSubmit={handleLogin}>
            <div>
              <Input
                label="کد تایید پیامک شده"
                type="text"
                placeholder="11111"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                required
                className="text-center tracking-[1em] text-lg"
                maxLength={7}
              />
            </div>
            {error && <p className="text-gray-600 text-sm text-center">{error}</p>}
            
            <div className="flex flex-col gap-3">
                <Button type="submit" fullWidth disabled={loading}>
                {loading ? 'در حال بررسی...' : 'تایید و ورود'}
                </Button>
                <button 
                    type="button" 
                    onClick={() => setStep('phone')}
                    className="flex items-center justify-center gap-1 text-sm text-gray-500 hover:text-gray-900"
                >
                    <ArrowLeft size={14} />
                    ویرایش شماره
                </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default LoginPage;